import { Button } from "@/lib/components/Button/Button";
import { Input } from "@/lib/components/Input";
import Toast from "@/lib/components/Toast";
import { queryClient } from "@/lib/queries/query-client";
import { useUpdateTeamMutation } from "@/lib/queries/team-queries";
import { userQueryKeys } from "@/lib/queries/user-queries";
import { useState } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogFooter,
} from "@/lib/components/Dialog";
import { useCurrentTeamMembership } from "@/lib/hooks/team-hooks";
import { TeamPermissions } from "@repo/api/team-roles";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/lib/components/Form";
import { useForm } from "react-hook-form";
import { UpdateTeamDto } from "@repo/api/team";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

export default function UpdateTeamDialog() {
  const [open, setOpen] = useState(false);
  const teamMembership = useCurrentTeamMembership();
  const teamExternalId = teamMembership?.teamExternalId;

  const { mutate: updateTeam, isPending } = useUpdateTeamMutation({
    teamExternalId: teamExternalId!,
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.all(),
        type: "all",
      });
      setOpen(false);
      Toast.info("Team updated successfully");
    },
    onError: () => {
      Toast.error("Failed to update team");
    },
  });

  const form = useForm<z.infer<typeof UpdateTeamDto>>({
    resolver: zodResolver(UpdateTeamDto),
    defaultValues: {
      name: teamMembership?.teamName || "",
    },
  });

  function onSubmit(values: z.infer<typeof UpdateTeamDto>) {
    updateTeam(values);
  }

  if (
    !teamMembership ||
    !teamMembership.permissions.includes(TeamPermissions.TEAM_EDIT) ||
    !teamExternalId
  ) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">Edit Team</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-lg">
        <DialogHeader className="w-full">
          <DialogTitle>Edit your team</DialogTitle>
          <DialogDescription>Update your team settings.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Team Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter team name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="mt-8">
              <Button type="submit" disabled={isPending} isLoading={isPending}>
                Save
              </Button>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
